import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { ArrowLeft, Save, Trash2, StickyNote, Edit2, Check, X, Camera, Package } from "lucide-react";
import { toast } from "sonner";

const ShipmentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { API, authHeader, token } = useAuth();
  const isEdit = !!id;

  const [suppliers, setSuppliers] = useState([]);
  const [transitAgents, setTransitAgents] = useState([]);
  const [shippingAgents, setShippingAgents] = useState([]);
  const [djiboutiTransporters, setDjiboutiTransporters] = useState([]);
  const [ethiopiaTransiters, setEthiopiaTransiters] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [itemName, setItemName] = useState("");
  const [itemQty, setItemQty] = useState("");
  const [itemPrice, setItemPrice] = useState("");
  const [itemPhotoName, setItemPhotoName] = useState("");
  const [itemPhotoKey, setItemPhotoKey] = useState(0);
  const [addingItem, setAddingItem] = useState(false);
  const itemPhotoRef = useRef();

  const [form, setForm] = useState({
    shipment_name: "",
    supplier_id: "",
    supplier_name: "",
    transit_agent_id: "",
    transit_agent_name: "",
    shipping_agent_id: "",
    shipping_agent_name: "",
    djibouti_transporter_id: "",
    djibouti_transporter_name: "",
    ethiopia_transiter_id: "",
    ethiopia_transiter_name: "",
    tracking_number: "",
    bill_of_lading: "",
    container_number: "",
    carrier: "",
    origin: "China",
    destination: "Ethiopia",
    status: "ordered",
    departure_date: "",
    arrival_date: "",
    product_cost_usd: 0,
    shipping_cost_usd: 0,
    customs_duty_usd: 0,
    djibouti_fees_usd: 0,
    ethiopia_transport_etb: 0,
    agent_fees_usd: 0,
    other_fees_usd: 0,
    usd_to_etb: 128.0,
    expected_revenue_etb: 0,
    actual_revenue_etb: 0,
    notes: "",
  });

  useEffect(() => {
    axios.get(`${API}/trade/suppliers`, { headers: authHeader() })
      .then(res => setSuppliers(res.data))
      .catch(e => console.error(e));

    axios.get(`${API}/trade/agents`, { headers: authHeader() })
      .then(res => {
        setTransitAgents(res.data.filter(a => a.role === "transit"));
        setShippingAgents(res.data.filter(a => a.role === "shipping"));
        setDjiboutiTransporters(res.data.filter(a => a.role === "djibouti_transporter"));
        setEthiopiaTransiters(res.data.filter(a => a.role === "ethiopia_transiter"));
      })
      .catch(e => console.error(e));

    if (isEdit) {
      axios.get(`${API}/trade/shipments/${id}`, { headers: authHeader() })
        .then(res => {
          setForm(res.data);
          setLoading(false);
        })
        .catch(() => {
          toast.error("Failed to load shipment");
          navigate("/trade/shipments");
        });
    }
  }, [id]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSupplierChange = (supplierId) => {
    const s = suppliers.find(x => x.id === supplierId);
    setForm(prev => ({
      ...prev,
      supplier_id: supplierId,
      supplier_name: s ? s.name : ""
    }));
  };

  const handleTransitAgentChange = (agentId) => {
    if (agentId === "_none") {
      setForm(prev => ({ ...prev, transit_agent_id: "", transit_agent_name: "" }));
      return;
    }
    const a = transitAgents.find(x => x.id === agentId);
    setForm(prev => ({
      ...prev,
      transit_agent_id: agentId,
      transit_agent_name: a ? a.name : ""
    }));
  };

  const handleShippingAgentChange = (agentId) => {
    if (agentId === "_none") {
      setForm(prev => ({ ...prev, shipping_agent_id: "", shipping_agent_name: "" }));
      return;
    }
    const a = shippingAgents.find(x => x.id === agentId);
    setForm(prev => ({
      ...prev,
      shipping_agent_id: agentId,
      shipping_agent_name: a ? a.name : ""
    }));
  };

  const handleDjiboutiTransporterChange = (agentId) => {
    if (agentId === "_none") {
      setForm(prev => ({ ...prev, djibouti_transporter_id: "", djibouti_transporter_name: "" }));
      return;
    }
    const a = djiboutiTransporters.find(x => x.id === agentId);
    setForm(prev => ({
      ...prev,
      djibouti_transporter_id: agentId,
      djibouti_transporter_name: a ? a.name : ""
    }));
  };

  const handleEthiopiaTransiterChange = (agentId) => {
    if (agentId === "_none") {
      setForm(prev => ({ ...prev, ethiopia_transiter_id: "", ethiopia_transiter_name: "" }));
      return;
    }
    const a = ethiopiaTransiters.find(x => x.id === agentId);
    setForm(prev => ({
      ...prev,
      ethiopia_transiter_id: agentId,
      ethiopia_transiter_name: a ? a.name : ""
    }));
  };

  const handleSave = async () => {
    if (!form.shipment_name) {
      toast.error("Shipment name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Convert numeric fields
      ["product_cost_usd", "shipping_cost_usd", "customs_duty_usd", "djibouti_fees_usd",
       "ethiopia_transport_etb", "agent_fees_usd", "other_fees_usd",
       "usd_to_etb", "expected_revenue_etb", "actual_revenue_etb"].forEach(k => {
        payload[k] = parseFloat(payload[k]) || 0;
      });
      // Normalize string fields
      ["bill_of_lading", "container_number", "carrier", "tracking_number"].forEach(k => {
        payload[k] = (payload[k] || "").trim();
      });

      if (isEdit) {
        await axios.put(`${API}/trade/shipments/${id}`, payload, { headers: authHeader() });
        toast.success("Shipment updated");
      } else {
        await axios.post(`${API}/trade/shipments`, payload, { headers: authHeader() });
        toast.success("Shipment created");
      }
      navigate("/trade/shipments");
    } catch (e) {
      toast.error("Failed to save shipment");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this shipment permanently?")) return;
    try {
      await axios.delete(`${API}/trade/shipments/${id}`, { headers: authHeader() });
      toast.success("Shipment deleted");
      navigate("/trade/shipments");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  // Shipment notes (saved immediately, only on existing shipments)
  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const res = await axios.post(`${API}/trade/shipments/${id}/notes`, { text: noteText.trim() }, { headers: authHeader() });
      setForm(f => ({ ...f, note_list: [...(f.note_list || []), res.data] }));
      setNoteText("");
      toast.success("Note saved");
    } catch {
      toast.error("Failed to save note");
    }
  };

  const saveNote = async (noteId) => {
    if (!editNoteText.trim()) return;
    try {
      await axios.put(`${API}/trade/shipments/${id}/notes/${noteId}`, { text: editNoteText.trim() }, { headers: authHeader() });
      setForm(f => ({ ...f, note_list: (f.note_list || []).map(n => n.id === noteId ? { ...n, text: editNoteText.trim() } : n) }));
      setEditingNoteId(null);
      setEditNoteText("");
      toast.success("Note updated");
    } catch {
      toast.error("Failed to update note");
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API}/trade/shipments/${id}/notes/${noteId}`, { headers: authHeader() });
      setForm(f => ({ ...f, note_list: (f.note_list || []).filter(n => n.id !== noteId) }));
      toast.success("Note deleted");
    } catch {
      toast.error("Failed to delete note");
    }
  };

  // Goods items (photo + quantity + unit price)
  const itemImageUrl = (item) => `${API}/trade/shipments/${id}/items/${item.id}/image?auth=${token}`;
  const itemsTotal = (form.items || []).reduce((sum, it) => sum + (it.quantity || 0) * (it.unit_price_usd || 0), 0);

  const addItem = async () => {
    const file = itemPhotoRef.current?.files?.[0];
    if (!file) {
      toast.error("Take or choose a photo of the goods first");
      return;
    }
    if (!itemName.trim()) {
      toast.error("Item name is required");
      return;
    }
    setAddingItem(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", itemName.trim());
    fd.append("quantity", itemQty || "1");
    fd.append("unit_price_usd", itemPrice || "0");
    try {
      const res = await axios.post(`${API}/trade/shipments/${id}/items`, fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" }
      });
      setForm(f => ({ ...f, items: [...(f.items || []), res.data] }));
      setItemName("");
      setItemQty("");
      setItemPrice("");
      setItemPhotoName("");
      if (itemPhotoRef.current) itemPhotoRef.current.value = "";
      setItemPhotoKey(k => k + 1);
      toast.success("Item added");
    } catch {
      toast.error("Failed to add item");
    } finally {
      setAddingItem(false);
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/trade/shipments/${id}/items/${itemId}`, { headers: authHeader() });
      setForm(f => ({ ...f, items: (f.items || []).filter(i => i.id !== itemId) }));
      toast.success("Item deleted");
    } catch {
      toast.error("Failed to delete item");
    }
  };

  // Calculated totals
  const productCostUSD = parseFloat(form.product_cost_usd) || 0;
  const ethTransportUSD = (form.usd_to_etb || 1) ? (form.ethiopia_transport_etb || 0) / form.usd_to_etb : 0;
  const totalCostUSD = productCostUSD + (parseFloat(form.shipping_cost_usd) || 0) + 
                       (parseFloat(form.customs_duty_usd) || 0) + (parseFloat(form.djibouti_fees_usd) || 0) + 
                       (parseFloat(form.agent_fees_usd) || 0) + (parseFloat(form.other_fees_usd) || 0) + 
                       ethTransportUSD;
  const totalCostETB = totalCostUSD * (form.usd_to_etb || 1);
  const profit = (parseFloat(form.actual_revenue_etb) || 0) - totalCostETB;

  if (loading) return <div className="trade-section pt-32 text-center">Loading...</div>;

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10">
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link to="/trade/shipments" className="inline-flex items-center gap-2 text-sm text-white/60 hover:trade-gold mb-6">
            <ArrowLeft size={16} /> Back to shipments
          </Link>
          <h1 className="text-3xl md:text-4xl font-serif font-bold">
            {isEdit ? "Edit Shipment" : "New Shipment"}
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Basic Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="trade-card rounded-sm p-6"
            >
              <h3 className="text-sm uppercase tracking-widest trade-gold mb-5">Basic Info</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-xs text-white/60">Shipment Name *</Label>
                  <Input
                    value={form.shipment_name}
                    onChange={e => handleChange("shipment_name", e.target.value)}
                    placeholder="e.g. Electronics Batch #1"
                    className="trade-input mt-1 h-11 rounded-sm"
                    data-testid="shipment-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Supplier</Label>
                  <Select value={form.supplier_id} onValueChange={handleSupplierChange}>
                    <SelectTrigger className="trade-input mt-1 h-11 rounded-sm">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#142039] border-white/10">
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Tracking Number</Label>
                  <Input
                    value={form.tracking_number}
                    onChange={e => handleChange("tracking_number", e.target.value)}
                    className="trade-input mt-1 h-11 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Carrier (e.g. Maersk, CMA CGM)</Label>
                  <Input
                    value={form.carrier}
                    onChange={e => handleChange("carrier", e.target.value)}
                    placeholder="Shipping line name"
                    className="trade-input mt-1 h-11 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Bill of Lading (B/L) Number</Label>
                  <Input
                    value={form.bill_of_lading}
                    onChange={e => handleChange("bill_of_lading", e.target.value)}
                    placeholder="e.g. HLCUXXXX1234567"
                    className="trade-input mt-1 h-11 rounded-sm font-mono"
                    data-testid="bl-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Container Number</Label>
                  <Input
                    value={form.container_number}
                    onChange={e => handleChange("container_number", e.target.value)}
                    placeholder="e.g. CMAU 1234567"
                    className="trade-input mt-1 h-11 rounded-sm font-mono"
                    data-testid="container-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Transit Agent</Label>
                  <Select value={form.transit_agent_id || "_none"} onValueChange={handleTransitAgentChange}>
                    <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="transit-agent-select">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#142039] border-white/10">
                      <SelectItem value="_none">None</SelectItem>
                      {transitAgents.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.name}{a.company ? ` · ${a.company}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Shipping Agent</Label>
                  <Select value={form.shipping_agent_id || "_none"} onValueChange={handleShippingAgentChange}>
                    <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="shipping-agent-select">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#142039] border-white/10">
                      <SelectItem value="_none">None</SelectItem>
                      {shippingAgents.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.name}{a.company ? ` · ${a.company}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Djibouti → Ethiopia Transporter</Label>
                  <Select value={form.djibouti_transporter_id || "_none"} onValueChange={handleDjiboutiTransporterChange}>
                    <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="djibouti-transporter-select">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#142039] border-white/10">
                      <SelectItem value="_none">None</SelectItem>
                      {djiboutiTransporters.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.name}{a.company ? ` · ${a.company}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Ethiopia Transiter</Label>
                  <Select value={form.ethiopia_transiter_id || "_none"} onValueChange={handleEthiopiaTransiterChange}>
                    <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="ethiopia-transiter-select">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#142039] border-white/10">
                      <SelectItem value="_none">None</SelectItem>
                      {ethiopiaTransiters.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.name}{a.company ? ` · ${a.company}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Status</Label>
                  <Select value={form.status} onValueChange={v => handleChange("status", v)}>
                    <SelectTrigger className="trade-input mt-1 h-11 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#142039] border-white/10">
                      <SelectItem value="ordered">Ordered</SelectItem>
                      <SelectItem value="in_transit_china">In Transit (China)</SelectItem>
                      <SelectItem value="in_transit_djibouti">In Transit (Djibouti)</SelectItem>
                      <SelectItem value="cleared_djibouti">Cleared Djibouti</SelectItem>
                      <SelectItem value="in_transit_ethiopia">In Transit (Ethiopia)</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Departure Date</Label>
                  <Input
                    type="date"
                    value={form.departure_date}
                    onChange={e => handleChange("departure_date", e.target.value)}
                    className="trade-input mt-1 h-11 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Arrival Date</Label>
                  <Input
                    type="date"
                    value={form.arrival_date}
                    onChange={e => handleChange("arrival_date", e.target.value)}
                    className="trade-input mt-1 h-11 rounded-sm"
                  />
                </div>
              </div>
            </motion.div>

            {/* Costs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="trade-card rounded-sm p-6"
            >
              <h3 className="text-sm uppercase tracking-widest trade-gold mb-5">Costs</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-white/60">Product Cost (USD $)</Label>
                  <Input type="number" step="0.01" value={form.product_cost_usd} onChange={e => handleChange("product_cost_usd", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" data-testid="product-cost-input" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Shipping (USD $)</Label>
                  <Input type="number" step="0.01" value={form.shipping_cost_usd} onChange={e => handleChange("shipping_cost_usd", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Customs Duty (USD $)</Label>
                  <Input type="number" step="0.01" value={form.customs_duty_usd} onChange={e => handleChange("customs_duty_usd", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Djibouti Fees (USD $)</Label>
                  <Input type="number" step="0.01" value={form.djibouti_fees_usd} onChange={e => handleChange("djibouti_fees_usd", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Ethiopia Transport (ETB)</Label>
                  <Input type="number" step="0.01" value={form.ethiopia_transport_etb} onChange={e => handleChange("ethiopia_transport_etb", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Agent Fees (USD $)</Label>
                  <Input type="number" step="0.01" value={form.agent_fees_usd} onChange={e => handleChange("agent_fees_usd", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-white/60">Other Fees (USD $)</Label>
                  <Input type="number" step="0.01" value={form.other_fees_usd} onChange={e => handleChange("other_fees_usd", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" />
                </div>
              </div>
            </motion.div>

            {/* Exchange Rates */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="trade-card rounded-sm p-6"
            >
              <h3 className="text-sm uppercase tracking-widest trade-gold mb-5">Exchange Rate</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-white/60">1 USD = ? ETB</Label>
                  <Input type="number" step="0.01" value={form.usd_to_etb} onChange={e => handleChange("usd_to_etb", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" />
                </div>
              </div>
            </motion.div>

            {/* Revenue */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="trade-card rounded-sm p-6"
            >
              <h3 className="text-sm uppercase tracking-widest trade-green mb-5">Revenue (ETB)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-white/60">Expected Revenue</Label>
                  <Input type="number" step="0.01" value={form.expected_revenue_etb} onChange={e => handleChange("expected_revenue_etb", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Actual Revenue</Label>
                  <Input type="number" step="0.01" value={form.actual_revenue_etb} onChange={e => handleChange("actual_revenue_etb", e.target.value)} className="trade-input mt-1 h-11 rounded-sm" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-white/60">General Remarks</Label>
                  <Textarea value={form.notes} onChange={e => handleChange("notes", e.target.value)} rows={4} className="trade-input mt-1 rounded-sm" />
                </div>
              </div>
            </motion.div>

            {/* Goods Items — photo, quantity and price for each (existing shipments) */}
            {isEdit && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="trade-card rounded-sm p-6"
                data-testid="goods-items-section"
              >
                <h3 className="text-sm uppercase tracking-widest trade-gold mb-5 flex items-center gap-2">
                  <Package size={14} /> Goods Items
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <div className="col-span-2">
                    <Label className="text-xs text-white/60">Item Name</Label>
                    <Input
                      value={itemName}
                      onChange={e => setItemName(e.target.value)}
                      placeholder="e.g. Jebena, Low Bed, Excavator"
                      className="trade-input mt-1 h-10 rounded-sm"
                      data-testid="item-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Quantity</Label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={itemQty}
                      onChange={e => setItemQty(e.target.value)}
                      className="trade-input mt-1 h-10 rounded-sm"
                      data-testid="item-qty-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Price Each (USD)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={itemPrice}
                      onChange={e => setItemPrice(e.target.value)}
                      className="trade-input mt-1 h-10 rounded-sm"
                      data-testid="item-price-input"
                    />
                  </div>
                </div>

                <div className="flex gap-2 mb-2">
                  <input
                    key={itemPhotoKey}
                    ref={itemPhotoRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={e => setItemPhotoName(e.target.files?.[0]?.name || "")}
                    className="hidden"
                    data-testid="item-photo-input"
                  />
                  <Button
                    onClick={() => itemPhotoRef.current?.click()}
                    className="flex-1 bg-white/10 text-white hover:bg-white/20 rounded-sm h-10 text-xs"
                    data-testid="item-photo-btn"
                  >
                    <Camera size={14} className="mr-2" />
                    {itemPhotoName ? itemPhotoName : "Photo of Goods"}
                  </Button>
                  <Button
                    onClick={addItem}
                    disabled={addingItem}
                    className="flex-1 trade-btn-primary rounded-sm h-10 text-xs"
                    data-testid="add-item-btn"
                  >
                    {addingItem ? "Adding..." : "Add Item"}
                  </Button>
                </div>

                {(form.items || []).length === 0 ? (
                  <p className="text-xs text-white/40 mt-4">No items yet — add goods with a photo, quantity and price</p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                      {(form.items || []).map(it => (
                        <div key={it.id} className="bg-white/5 rounded-sm overflow-hidden" data-testid={`item-card-${it.id}`}>
                          <img
                            src={itemImageUrl(it)}
                            alt={it.name}
                            className="w-full h-28 object-cover"
                            data-testid={`item-image-${it.id}`}
                          />
                          <div className="p-3">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-semibold truncate">{it.name}</p>
                              <button
                                onClick={() => deleteItem(it.id)}
                                className="text-red-400/70 hover:text-red-400 transition-colors flex-shrink-0"
                                data-testid={`delete-item-${it.id}`}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                            <p className="text-[11px] text-white/50 mt-1">
                              {it.quantity} × ${(it.unit_price_usd || 0).toLocaleString()}
                            </p>
                            <p className="text-xs trade-gold font-mono font-semibold mt-0.5">
                              ${((it.quantity || 0) * (it.unit_price_usd || 0)).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between flex-wrap gap-3 mt-4 pt-4 border-t border-white/10">
                      <p className="text-sm" data-testid="items-total">
                        Items total: <span className="trade-gold font-mono font-bold">${itemsTotal.toLocaleString('en-US', {maximumFractionDigits: 2})}</span>
                      </p>
                      <button
                        onClick={() => setForm(f => ({ ...f, product_cost_usd: Math.round(itemsTotal * 100) / 100 }))}
                        className="text-xs trade-green hover:underline"
                        data-testid="use-as-cost-btn"
                      >
                        Use as Product Cost
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            )}

            {/* Shipment Notes — saved individually, only on existing shipments */}
            {isEdit && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="trade-card rounded-sm p-6"
                data-testid="shipment-notes-section"
              >
                <h3 className="text-sm uppercase tracking-widest trade-gold mb-5 flex items-center gap-2">
                  <StickyNote size={14} /> Notes
                </h3>
                <div className="flex gap-2 mb-4">
                  <Input
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addNote()}
                    placeholder="Write a note — saved instantly"
                    className="trade-input h-11 rounded-sm flex-1"
                    data-testid="note-input"
                  />
                  <Button onClick={addNote} className="trade-btn-primary rounded-sm h-11 px-5" data-testid="add-note-btn">
                    Add
                  </Button>
                </div>
                {(form.note_list || []).length === 0 ? (
                  <p className="text-xs text-white/40">No notes yet</p>
                ) : (
                  <div className="space-y-2">
                    {(form.note_list || []).map(n => (
                      <div key={n.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-sm" data-testid={`note-${n.id}`}>
                        {editingNoteId === n.id ? (
                          <>
                            <Input
                              value={editNoteText}
                              onChange={e => setEditNoteText(e.target.value)}
                              className="trade-input h-9 rounded-sm flex-1 text-sm"
                              data-testid={`edit-note-input-${n.id}`}
                            />
                            <button
                              onClick={() => saveNote(n.id)}
                              className="p-2 trade-green hover:bg-[#10b981]/10 rounded-sm transition-colors"
                              data-testid={`save-note-${n.id}`}
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => { setEditingNoteId(null); setEditNoteText(""); }}
                              className="p-2 text-white/50 hover:bg-white/5 rounded-sm transition-colors"
                            >
                              <X size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            <p className="text-sm text-white/80 flex-1 whitespace-pre-wrap">{n.text}</p>
                            <span className="text-[10px] text-white/40 flex-shrink-0">
                              {new Date(n.created_at).toLocaleDateString()}
                            </span>
                            <button
                              onClick={() => { setEditingNoteId(n.id); setEditNoteText(n.text); }}
                              className="p-2 text-white/50 hover:text-[#D4AF37] transition-colors"
                              data-testid={`edit-note-${n.id}`}
                            >
                              <Edit2 size={13} />
                            </button>
                            <button
                              onClick={() => deleteNote(n.id)}
                              className="p-2 text-red-400/70 hover:text-red-400 transition-colors"
                              data-testid={`delete-note-${n.id}`}
                            >
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Summary Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:sticky lg:top-28 space-y-6 h-fit"
          >
            <div className="trade-card rounded-sm p-6">
              <h3 className="text-sm uppercase tracking-widest trade-gold mb-5">Cost Summary</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Product (USD)</span>
                  <span className="font-mono">${productCostUSD.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Shipping</span>
                  <span className="font-mono">${(parseFloat(form.shipping_cost_usd) || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Customs + Djibouti</span>
                  <span className="font-mono">${((parseFloat(form.customs_duty_usd) || 0) + (parseFloat(form.djibouti_fees_usd) || 0)).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Agent + Other</span>
                  <span className="font-mono">${((parseFloat(form.agent_fees_usd) || 0) + (parseFloat(form.other_fees_usd) || 0)).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">ET Transport (USD)</span>
                  <span className="font-mono">${ethTransportUSD.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                <div className="pt-3 border-t border-white/10 flex justify-between">
                  <span className="trade-gold font-semibold">Total (USD)</span>
                  <span className="trade-gold font-mono font-bold">${totalCostUSD.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between">
                  <span className="trade-gold font-semibold">Total (ETB)</span>
                  <span className="trade-gold font-mono font-bold">{totalCostETB.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                </div>
                <div className={`pt-3 border-t border-white/10 flex justify-between text-lg`}>
                  <span className={profit >= 0 ? "trade-green font-semibold" : "text-red-400 font-semibold"}>Profit</span>
                  <span className={`font-mono font-bold ${profit >= 0 ? "trade-green" : "text-red-400"}`}>
                    {profit >= 0 ? "+" : ""}ETB {profit.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full trade-btn-primary rounded-sm h-12"
              data-testid="save-shipment-btn"
            >
              <Save size={16} className="mr-2" />
              {saving ? "Saving..." : (isEdit ? "Update Shipment" : "Create Shipment")}
            </Button>

            {isEdit && (
              <Button
                onClick={handleDelete}
                variant="outline"
                className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm h-11"
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </Button>
            )}
          </motion.div>
        </div>

      </div>
    </div>
  );
};

export default ShipmentForm;
